import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import { useState } from 'react';
import {
  MoreVert,
  Edit,
  Delete,
  Person,
  Phone,
  Email,
  Store,
  Public,
  PersonOff,
  PersonAddAlt1,
  Call,
  WhatsApp,
  Send,
  HowToReg,
  MailOutline,
  Refresh,
  SupervisorAccount,
  PersonRemove,
} from '@mui/icons-material';
import { Employee, AccountStatus } from '../../types/employee.types';
import { useShops } from '../../hooks/useShops';

interface EmployeeCardProps {
  employee: Employee;
  onEdit: (employee: Employee) => void;
  onDelete: (employee: Employee) => void;
  onMarkInactive: (employee: Employee) => void;
  onInvite?: (employee: Employee) => void;
  onResendInvite?: (employee: Employee) => void;
  onPromote?: (employee: Employee) => void;
  onDemote?: (employee: Employee) => void;
  isPromoted?: boolean; // Whether the employee has been promoted to a staff role
}

export default function EmployeeCard({ employee, onEdit, onDelete, onMarkInactive, onInvite, onResendInvite, onPromote, onDemote, isPromoted }: EmployeeCardProps) {
  const { shops } = useShops();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(employee);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(employee);
    handleMenuClose();
  };

  const handleMarkInactive = () => {
    onMarkInactive(employee);
    handleMenuClose();
  };

  const handleInvite = () => {
    if (onInvite) {
      onInvite(employee);
    }
    handleMenuClose();
  };

  const handleResendInvite = () => {
    if (onResendInvite) {
      onResendInvite(employee);
    }
    handleMenuClose();
  };

  const handlePromote = () => {
    if (onPromote) {
      onPromote(employee);
    }
    handleMenuClose();
  };

  const handleDemote = () => {
    if (onDemote) {
      onDemote(employee);
    }
    handleMenuClose();
  };

  // Get account status display info
  const getAccountStatusInfo = (status?: AccountStatus) => {
    switch (status) {
      case 'active':
        return { label: 'Has Account', color: 'success' as const, icon: <HowToReg fontSize="small" /> };
      case 'invited':
        return { label: 'Invited', color: 'info' as const, icon: <MailOutline fontSize="small" /> };
      default:
        return null; // No badge for employees without account/invite
    }
  };

  const accountStatusInfo = getAccountStatusInfo(employee.accountStatus);

  const getInitials = (name: string, maxChars = 2) =>
    name
      .trim()
      .split(/\s+/)
      .map(word => word[0])
      .join('')
      .slice(0, maxChars)
      .toUpperCase();

  // Get assigned shops
  const getAssignedShops = () => {
    if (!employee.assignedShopIds || employee.assignedShopIds.length === 0) {
      return [];
    }
    return employee.assignedShopIds
      .map(shopId => shops.find(s => s.id === shopId))
      .filter(shop => shop !== undefined);
  };

  const assignedShops = getAssignedShops();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'primary.light',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Name and menu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              {getInitials(employee.name)}
            </Avatar>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {employee.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Email */}
        <Box sx={{ display: 'grid', gap: 0.75, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: employee.email ? 'normal' : 'italic',
              }}
            >
              {employee.email || 'Not Provided'}
            </Typography>
          </Box>

          {/* Phone */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: employee.phone ? 'normal' : 'italic',
              }}
            >
              {employee.phone || 'Not Provided'}
            </Typography>
          </Box>
        </Box>

        {/* Assigned Shops and Status */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {assignedShops.length === 0 ? (
            <Chip
              icon={<Store fontSize="small" />}
              label="No shops assigned"
              size="small"
              color="default"
              variant="outlined"
            />
          ) : assignedShops.length === shops.length ? (
            <Chip
              icon={<Public fontSize="small" />}
              label="All Shops"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : (
            assignedShops.map(shop => (
              <Chip
                key={shop.id}
                icon={<Store fontSize="small" />}
                label={shop.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))
          )}
          {employee.status === 'active' ? (
            <Chip
              icon={<Person fontSize="small" />}
              label="Active"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<PersonOff fontSize="small" />}
              label="Inactive"
              size="small"
              color="default"
              variant="outlined"
            />
          )}
          {/* Account Status Badge */}
          {accountStatusInfo && (
            <Chip
              icon={accountStatusInfo.icon}
              label={accountStatusInfo.label}
              size="small"
              color={accountStatusInfo.color}
              variant="filled"
            />
          )}
          {/* Promoted Badge */}
          {isPromoted && (
            <Chip
              icon={<SupervisorAccount sx={{ fontSize: 14 }} />}
              label="Manager"
              size="small"
              color="warning"
              variant="filled"
            />
          )}
        </Box>
      </CardContent>

      <Divider />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 0.75,
          p: 1.5,
        }}
      >
        {/* Call */}
        <Button
          fullWidth
          size="small"
          disabled={!employee.phone}
          component="a"
          href={employee.phone ? `tel:${employee.phone}` : undefined}
          sx={{
            bgcolor: employee.phone ? 'success.dark' : 'action.disabledBackground',
            color: employee.phone ? 'common.white' : 'text.disabled',
            fontSize: '0.75rem',
            minWidth: 0,
            px: 1,
            '&:hover': {
              bgcolor: employee.phone ? 'success.main' : 'action.disabledBackground',
            },
          }}
        >
          <Call sx={{ fontSize: 16, mr: 0.5 }} />
          Call
        </Button>

        {/* WhatsApp */}
        <Button
          fullWidth
          size="small"
          disabled={!employee.phone}
          component="a"
          href={
            employee.phone
              ? `https://wa.me/${employee.phone.replace(/\D/g, '')}`
              : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            bgcolor: employee.phone ? '#25D366' : 'action.disabledBackground',
            color: employee.phone ? 'common.white' : 'text.disabled',
            fontSize: '0.75rem',
            minWidth: 0,
            px: 1,
            '&:hover': {
              bgcolor: employee.phone ? '#1ebe5d' : 'action.disabledBackground',
            },
          }}
        >
          <WhatsApp sx={{ fontSize: 16, mr: 0.5 }} />
          Chat
        </Button>

        {/* Email */}
        <Button
          fullWidth
          size="small"
          disabled={!employee.email}
          component="a"
          href={employee.email ? `mailto:${employee.email}` : undefined}
          sx={{
            bgcolor: employee.email ? 'primary.main' : 'action.disabledBackground',
            color: employee.email ? 'common.white' : 'text.disabled',
            fontSize: '0.75rem',
            minWidth: 0,
            px: 1,
            '&:hover': {
              bgcolor: employee.email ? 'primary.dark' : 'action.disabledBackground',
            },
          }}
        >
          <Email sx={{ fontSize: 16, mr: 0.5 }} />
          Email
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem>
        {/* Invite option - show for employees without account who have email */}
        {employee.email && employee.accountStatus !== 'active' && employee.accountStatus !== 'invited' && onInvite && (
          <MenuItem onClick={handleInvite} sx={{ color: 'primary.main' }}>
            <ListItemIcon>
              <Send fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Get Invitation Link</ListItemText>
          </MenuItem>
        )}
        {/* Get new link option - show for employees with pending invitation */}
        {employee.accountStatus === 'invited' && onResendInvite && (
          <MenuItem onClick={handleResendInvite} sx={{ color: 'info.main' }}>
            <ListItemIcon>
              <Refresh fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Get New Invitation Link</ListItemText>
          </MenuItem>
        )}
        {/* Promotion/Demotion options - only for employees with active accounts */}
        {employee.accountStatus === 'active' && !isPromoted && onPromote && (
          <MenuItem onClick={handlePromote} sx={{ color: 'warning.main' }}>
            <ListItemIcon>
              <SupervisorAccount fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Promote to Manager</ListItemText>
          </MenuItem>
        )}
        {employee.accountStatus === 'active' && isPromoted && onDemote && (
          <MenuItem onClick={handleDemote} sx={{ color: 'secondary.main' }}>
            <ListItemIcon>
              <PersonRemove fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText>Demote to Employee</ListItemText>
          </MenuItem>
        )}
        {employee.status === 'active' ? (
          <MenuItem onClick={handleMarkInactive} sx={{ color: 'warning.main' }}>
            <ListItemIcon>
              <PersonOff fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Mark as Inactive</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleMarkInactive} sx={{ color: 'success.main' }}>
            <ListItemIcon>
              <PersonAddAlt1 fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Mark as Active</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Employee</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}
